import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { currencyApi, type Currency } from '@/services/currencyApi';

interface CurrencyContextType {
  currency: Currency | null;
  currencies: Currency[];
  isLoading: boolean;
  setCurrency: (currency: Currency) => Promise<void>;
  formatAmount: (amount: number) => string;
  convertToUSD: (amount: number) => Promise<number>;
  convertFromUSD: (amount: number) => Promise<number>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load currencies and user preference on mount
  useEffect(() => {
    const loadCurrencyData = async () => {
      try {
        setIsLoading(true);
        
        // Load all currencies
        const currenciesRes = await currencyApi.getCurrencies();
        if (currenciesRes.success) {
          setCurrencies(currenciesRes.data.currencies);
        }

        // Load user preference
        const userRes = await currencyApi.getUserCurrency();
        if (userRes.success) {
          setCurrencyState(userRes.data.currency);
        } else {
          // Fallback to default
          const defaultRes = await currencyApi.getDefaultCurrency();
          if (defaultRes.success) {
            setCurrencyState(defaultRes.data.currency);
          }
        }
      } catch (error) {
        console.error('Failed to load currency data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCurrencyData();
  }, []);

  // Update currency preference
  const setCurrency = useCallback(async (newCurrency: Currency) => {
    try {
      const response = await currencyApi.updateUserCurrency(newCurrency.code);
      if (response.success) {
        setCurrencyState(newCurrency);
      }
    } catch (error) {
      console.error('Failed to update currency:', error);
      throw error;
    }
  }, []);

  // Format amount with current currency
  const formatAmount = useCallback((amount: number): string => {
    if (!currency) return `$${amount.toFixed(2)}`;
    return currencyApi.formatAmount(amount, currency.code);
  }, [currency]);

  // Convert from current currency to USD
  const convertToUSD = useCallback(async (amount: number): Promise<number> => {
    if (!currency || currency.code === 'USD') return amount;
    try {
      const result = await currencyApi.convert(amount, currency.code, 'USD');
      return result.success ? result.data.convertedAmount : amount;
    } catch {
      return amount;
    }
  }, [currency]);

  // Convert from USD to current currency
  const convertFromUSD = useCallback(async (amount: number): Promise<number> => {
    if (!currency || currency.code === 'USD') return amount;
    try {
      const result = await currencyApi.convert(amount, 'USD', currency.code);
      return result.success ? result.data.convertedAmount : amount;
    } catch {
      return amount;
    }
  }, [currency]);

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        currencies,
        isLoading,
        setCurrency,
        formatAmount,
        convertToUSD,
        convertFromUSD,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

export default CurrencyContext;
